import React, { useRef, useEffect } from "react";
import { chooseCmpxGraphNavAdapter } from "./choose-cmpx-graph-nav-adapter";

import VisualSettings from "./VisualizatorSettingsNav.json";

const graphWrapStyle = {
    height: "100%",
    width: "100%",
    position: "relative",
    backgroundColor: "white"
};

export const ChooseCmpxGraphNav = props => {
    const canvasRef = useRef();
    const isMounted = useRef(false);
    const graphAdapter = useRef(null);
    const latestProps = useRef(props);
    const {
        signalData,
        signalStart,
        mainGraphWidth,
        cursorPosition,
        start_pnt,
        peaks,
        refreshFlag,
        userSweepUnits,
        signalRhythmStart,
        fd,
        setNavGraphWidth,
        setSignalRhythmStart
    } = props;

    useEffect(() => {
        const mount = async () => {
            graphAdapter.current = new chooseCmpxGraphNavAdapter({
                canvasRef: canvasRef.current,
                visualSettings: VisualSettings,
                fd: fd
            });

            graphAdapter.current.changeDefaultFrequencySettungs(fd);
            await graphAdapter.current.initializeGraph();
            await graphAdapter.current.setupRectangles();
            graphAdapter.current.setUserSweepUnits(userSweepUnits);

            setNavGraphWidth(graphAdapter.current.getWidthInPoints(fd));
            graphAdapter.current.onResize(onResize);

            isMounted.current = true;
        };

        mount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const update = async () => {
            if (signalData.length && mainGraphWidth) {
                Promise.all([updateGraph(), updateAreaRectnalge(), updateHoverRectangle(), updateSelectRectangle()]);
            }
        };

        update();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signalData, mainGraphWidth, refreshFlag]);

    useEffect(() => {
        if (cursorPosition) {
            updateHoverRectangle();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start_pnt, peaks]);

    useEffect(() => {
        if (isMounted.current) {
            graphAdapter.current.setUserSweepUnits(userSweepUnits);
        }
    }, [userSweepUnits]);

    useEffect(() => {
        latestProps.current = props;
    });

    const updateGraph = async () => {
        await Promise.all([graphAdapter.current.clearGraph()]);
        await graphAdapter.current.plotData(signalData);
    };

    const updateAreaRectnalge = () => {
        const { signalLength } = props;

        let newStart = signalStart;
        const proposedEnd = signalStart + mainGraphWidth;

        if (proposedEnd > signalLength) {
            newStart = signalLength - mainGraphWidth;
        }

        if (signalRhythmStart !== 0) {
            newStart = newStart % signalRhythmStart;
        }

        newStart = Math.max(0, newStart);

        graphAdapter.current.moveAreaRectangle(newStart, mainGraphWidth);
    };

    const updateHoverRectangle = () => {
        const { start_pnt, end_pnt } = props;

        const _start_pnt = start_pnt + signalStart - signalRhythmStart;
        const _width_pnt = end_pnt - start_pnt;

        graphAdapter.current.moveHoverRectangle(_start_pnt, _width_pnt + 1);
    };

    const updateSelectRectangle = () => {
        const { Q, S, fd } = props;
        const start = Q - 0.25 * fd - signalRhythmStart;
        const end = S + 0.25 * 2 * fd;

        graphAdapter.current.moveSelectRectangle(start, end + 1);
    };

    const onResize = async () => {
        const { signalStart, mainGraphWidth } = latestProps.current;

        const graphWidth = graphAdapter.current.getWidthInPoints(fd);
        setNavGraphWidth(graphWidth);
        // Calculate adjustment coeff for mainGraphWidth windows shrinking due to resize
        const sm = graphAdapter.current.valueConverter.convertMmToPoints(2, fd);
        setSignalRhythmStart(Math.floor(signalStart / (graphWidth - mainGraphWidth + sm)) * (graphWidth - mainGraphWidth + sm));
    };

    return <div className="canvas-wrap" style={graphWrapStyle} ref={canvasRef} />;
};
